import React, { useEffect, useRef } from 'react'
import { X, ArrowLeft } from 'lucide-react'

interface ModalProps {
    isOpen: boolean
    onClose: () => void
    title: string | React.ReactNode
    children: React.ReactNode
    className?: string
    confirmationText?: string
    onConfirm?: () => void
    showConfirmation?: boolean
    onCancelConfirmation?: () => void
    displayStyle?: 'cover' | 'page' | 'modal'
    showBackButton?: boolean
    onBack?: () => void
    showSkip?: boolean
}

const Modal: React.FC<ModalProps> = ({ 
    isOpen, 
    onClose, 
    title, 
    children, 
    className,
    confirmationText,
    onConfirm,
    showConfirmation = false,
    onCancelConfirmation,
    displayStyle = 'modal',
    showBackButton = false,
    onBack,
    showSkip = false
}) => {
    const modalRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
          if (!modalRef.current) return;
      
          const target = event.target as HTMLElement;
      
          // If the click is NOT inside the modal
          // AND also NOT inside any Radix popper content, close the modal.
          if (
            !modalRef.current.contains(target) &&
            !target.closest("[data-radix-popper-content-wrapper]") &&
            !target.closest("[data-radix-portal]") && 
            !modalRef.current.contains(event.target as Node) &&
            !target.closest("[data-radix-trigger]")
            && document.body.contains(event.target as Node)
            && !className?.includes('no-clickoutside')
            //&& !className?.includes('allow-popper')
          ) {
            event.stopPropagation()
            event.preventDefault()
            onClose();
          }
        };
      
        document.addEventListener("pointerdown", handleClickOutside);
        return () => {
          document.removeEventListener("pointerdown", handleClickOutside);
        };
      }, [onClose, className]);

    if (!isOpen) return null;
    
    return (
        <div 
            className="screen-cover flex items-center justify-center bg-black bg-opacity-50" 
            tabIndex={-1}
        >
            <div
                className="modal-body w-full h-full flex items-center justify-center"
                role="dialog"
                aria-modal="true"
                aria-labelledby={`${title}-title`}
                tabIndex={-1}
            >
                <div 
                    className={`relative w-full h-full max-h-[100vh] sm:h-auto sm:max-w-md sm:max-h-[90vh] sm:overflow-auto bg-eoe-bg dark:bg-eoe-dark-bg-modal px-6 sm:rounded-2xl ${className}`} 
                    ref={modalRef}
                >
                    <div className="modal-header flex justify-between items-center mb-6">
                            {showBackButton && onBack && (
                                <button 
                                    onClick={onBack}
                                    className="modal-back-button size-7 rounded-full text-eoe-text dark:text-eoe-dark-text hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover"
                                    aria-label="Go back"
                                >
                                    <span className="flex items-center justify-center">
                                        <span className="flex">
                                            <ArrowLeft className="size-6 stroke-1" />
                                        </span>
                                    </span>
                                </button>
                            )}
                        {showSkip ? (
                            <button
                                className="modal-close-button right-6 top-6 text-sm font-semibold text-eoe-text-meta dark:text-eoe-dark-text-meta hover:text-eoe-text dark:hover:text-eoe-dark-text"
                                onClick={onClose}
                                aria-label="Skip verification"
                            >
                                Skip
                            </button>
                        ) : (
                            <button
                                className={`${showBackButton ? '' : 'left-2 sm:left-auto' } modal-close-button size-7 rounded-full bg-eoe-icon dark:bg-eoe-dark-icon hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover`}
                                onClick={onClose}
                                aria-label={`Close ${title} Modal`}
                            >
                                <span className="flex items-center justify-center">
                                    <span className="flex">
                                        <X className="size-6 stroke-1" />
                                    </span>
                                </span>
                            </button>
                        )}
                    </div>

                    <div className="modal-title flex items-center flex-grow pt-4">
                        <h2 id={`${title}-title`} className="text-eoe-text-heading dark:text-eoe-dark-text-heading text-xl font-semibold">
                            {title}
                        </h2>
                    </div>
                    {children}

                    {/* Confirmation Dialog */}
                    {showConfirmation && confirmationText && (
                        <div className="modal-dialog fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-50">
                            <div className="modal-dialog-content bg-eoe-bg dark:bg-eoe-dark-bg-modal p-6 rounded-lg max-w-sm w-full mx-4">
                                <p className="text-eoe-text dark:text-eoe-dark-text mb-6">{confirmationText}</p>
                                <div className="flex justify-end space-x-2">
                                    <button 
                                        className="btn-secondary px-4 py-2" 
                                        onClick={onCancelConfirmation}
                                    >
                                        No
                                    </button>
                                    <button 
                                        className="btn-primary px-4 py-2" 
                                        onClick={onConfirm}
                                    >
                                        Yes
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}

export default Modal;
